import classNames from "classnames";
import * as React from "react";

import LabValuesTableRow from "./LabValuesTable/LabValuesTableRow";
import {
  backgroundStyleFromNode,
  newAddNodeChange,
  newDeleteNodeChange,
} from "../../../helpers/Doc/changes";

interface LabValuesTableProps
  extends DocLabValuesTableType<InlineNode>,
    BlockNodeAll {}

const ALWAYS_HEADERS = ["Lab", "Result", "Reference Range"];
const EDIT_ONLY_HEADERS = ["Result Status", "Reference Range Interval"];

const DoesNotDisplayToEndUserText = ({
  headerName,
}: {
  headerName: string;
}) => {
  if (EDIT_ONLY_HEADERS.includes(headerName)) {
    return (
      <>
        <br />
        <span className="lab-values-table-does-not-display-text">
          (does not display to end user)
        </span>
      </>
    );
  }
};

function LabValuesTable(props: LabValuesTableProps) {
  const { editingContext } = props;
  // Compose headers based on editing mode
  const headers = editingContext.isEditing
    ? [
        ALWAYS_HEADERS[0],
        ALWAYS_HEADERS[1],
        ...EDIT_ONLY_HEADERS,
        ALWAYS_HEADERS[2],
      ]
    : ALWAYS_HEADERS;

  const rows = props.rows.map((row, rowIndex) => {
    return (
      <LabValuesTableRow
        key={row.uid}
        row={row}
        uid={row.uid}
        editingContext={editingContext}
        userContext={props.userContext}
        displayContext={props.displayContext}
        currentVersion={props.currentVersion}
        documentContext={props.documentContext}
        featureFlag={props.featureFlag}
        mediaContext={props.mediaContext}
        rowIndex={rowIndex}
        parentTableUID={props.uid}
        blockChangeHighlight={row.blockChangeHighlight}
      />
    );
  });

  const addRow = editingContext.isEditing ? (
    <tr>
      <td colSpan={headers.length + 1} style={{ padding: 0 }}>
        <div className="lab-values-table-add-row-container">
          <hr className="lab-values-table-add-row-left-horizontal" />
          <button
            type="button"
            className="lab-values-table-add-row-button no-document-selection"
            onClick={() => {
              editingContext.clearCurrentDocSelection();
              const newNode = editingContext.nodeDefinitions[
                "labValuesTableRow"
              ].generate(editingContext.nodeDefinitions, {});
              editingContext.addChange(
                newAddNodeChange(newNode, props.uid, "rows", props.rows.length) // `rows` is the key for the array of rows in lab values table data structure
              );
            }}
          >
            <i className="fa fa-plus" style={{ marginRight: 6 }}></i>
            Add Row
          </button>
          <hr className="lab-values-table-add-row-right-horizontal" />
        </div>
      </td>
    </tr>
  ) : null;

  const deleteRowCell = editingContext.isEditing ? (
    <div className="lab-values-table-remove-button-container">
      <button
        type="button"
        onClick={() => {
          editingContext.clearCurrentDocSelection();
          editingContext.addChange(newDeleteNodeChange(props.uid));
        }}
        className="lab-values-table-remove-table-button lab-values-table-remove-row-button no-document-selection"
      >
        <i className="fa fa-times" />
      </button>
    </div>
  ) : null;

  return (
    <div
      id={props.uid}
      data-is-node
      data-uid={props.uid}
      className={classNames(
        props.simple ? "doc-table-simple" : "doc-table",
        props.uid,
        "lab-values-table-scroll-container"
      )}
      style={{
        ...backgroundStyleFromNode(props),
        marginRight: editingContext.isEditing ? 0 : undefined,
      }}
      contentEditable={false}
    >
      <table className="lab-values-table">
        <thead className="lab-values-table-header">
          <tr className="lab-values-table-header-row">
            {headers.map((header, index) => (
              <th
                key={header}
                className={
                  `lab-values-table-header-cell ${index === 0 ? "lab-values-table-header-cell--wide" : ""}`.trim()
                }
              >
                {header}
                <DoesNotDisplayToEndUserText headerName={header} />
              </th>
            ))}
            {editingContext.isEditing && (
              <th
                className={
                  editingContext.isEditing
                    ? "lab-values-table-remove-button-cell"
                    : "lab-values-table-header-cell"
                }
              >
                {deleteRowCell}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="lab-values-table-body">
          {rows}
          {addRow}
        </tbody>
      </table>
    </div>
  );
}

export default React.memo(LabValuesTable);
